/* eslint-disable eslint-comments/no-unlimited-disable */
/* eslint-disable unicorn/no-abusive-eslint-disable */
/* eslint-disable */
// @ts-nocheck

import { createEditor } from 'prism-code-editor';
import { matchBrackets } from 'prism-code-editor/match-brackets';
import { indentGuides } from 'prism-code-editor/guides';
import { highlightBracketPairs } from 'prism-code-editor/highlight-brackets';
import { insertText } from 'prism-code-editor/utils';
import { lightColors, darkColors } from '@tryhackme/thm-ui-components';

import 'prism-code-editor/prism/languages/markup';
import 'prism-code-editor/prism/languages/markdown';

import 'prism-code-editor/layout.css';
import 'prism-code-editor/scrollbar.css';

import { getEditorModalStyles, getStyles } from '../dark-mode-styles';

interface FormatCodeOptions {
  parser?: string;
}

function formatIndentation(code: string): string {
  let formatted = '';
  let indent = 0;
  const tab = '  '; // 2 spaces for indentation

  // Split on < to get array of tags and content
  const tokens = code.split('<');

  tokens.forEach((token, index) => {
    if (!token) return;

    // Add back the < that was removed by split
    const line = '<' + token;

    // Decrease indent for closing tags
    if (line.match(/^<\//)) {
      indent--;
    }

    // Add formatted line
    if (line.trim()) {
      formatted += tab.repeat(Math.max(0, indent)) + line.trim() + '\n';
    }

    // Increase indent for opening tags, but not for self-closing or void elements
    if (
      !line.match(/^<\//) && // not a closing tag
      !line.match(/\/>$/) && // not a self-closing tag
      !line.match(/^<(area|base|br|col|embed|hr|img|input|link|meta|param|source|track|wbr)[\s>]/i) && // not a void element
      line.match(/^<\w/) // is an opening tag
    ) {
      indent++;
    }
  });

  return formatted.trim();
}

export const formatCode = async (code: string, parser: string = 'html'): Promise<string> => {
  try {
    if (parser === 'html') {
      return formatIndentation(code);
    } else if (parser === 'markdown') {
      // For markdown, we'll keep the original formatting for now
      return code;
    }
    return code;
  } catch (error) {
    console.error('Error formatting code:', error);
    return code;
  }
};

export class BaseEditor {
  editor = null;
  containerId = '';
  initialContent = '';
  language = 'html';
  isDark = false;

  constructor(containerId: string, initialContent: string, language = 'html', isDark = false) {
    this.containerId = containerId;
    this.initialContent = initialContent;
    this.language = language;
    this.isDark = isDark;
  }

  async initialize() {
    const formattedContent = this.initialContent;

    const container = document.getElementById(this.containerId);
    if (!container) {
      throw new Error(`Container with id ${this.containerId} not found`);
    }

    this.editor = createEditor(
      `#${this.containerId}`,
      {
        language: this.language,
        value: formattedContent,
        lineNumbers: true,
        readOnly: false,
      },
      indentGuides(),
      matchBrackets(),
      highlightBracketPairs(),
    );

    return this;
  }

  async formatCurrentCode() {
    if (!this.editor) return;

    const currentCode = this.editor.value;
    const formattedCode = await formatCode(currentCode, this.language);
    const [start, end] = this.editor.getSelection();
    insertText(this.editor, formattedCode, 0, currentCode.length, start, end);
  }

  getValue() {
    return this.editor ? this.editor.value : '';
  }

  destroy() {
    if (this.editor) {
      const editorElement = document.querySelector(`#${this.containerId}`);
      if (editorElement) {
        editorElement.innerHTML = '';
        const newElement = editorElement.cloneNode(true);
        editorElement.parentNode.replaceChild(newElement, editorElement);
      }
      this.editor = null;
    }
  }
}

export class EditorModal {
  modal: HTMLDivElement;
  constructor(title = '', isDarkMode = false) {
    this.styles = getStyles(isDarkMode); // Ensure styles are initialized with the correct mode
    this.modal = document.createElement('div');
    this.modal.className = 'editor-modal';
    this.modal.style.cssText = this.styles.modal;
    // Create and add scoped styles
    this.styleElement = document.createElement('style');
    this.styleElement.textContent = getEditorModalStyles(isDarkMode);

    this.modal.appendChild(this.styleElement);
    if (title) {
      const header = document.createElement('div');
      header.style.cssText = this.styles.header;
      const titleEl = document.createElement('div');
      titleEl.textContent = title;
      titleEl.style.cssText = 'font-weight: bold; font-size: 16px;';
      header.appendChild(titleEl);
      this.modal.appendChild(header);
    }
  }

  createContainer(id) {
    const container = document.createElement('div');
    container.id = id;
    container.style.cssText = this.styles.editorContainer;
    return container;
  }

  createButton(text, variant, onClick) {
    const button = document.createElement('button');
    button.textContent = text;
    button.onclick = onClick;
    button.style.cssText = `${this.styles.button.base} ${this.styles.button[variant]}`;
    return button;
  }

  addEscapeHandler() {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        this.destroy();
        document.removeEventListener('keydown', handleEsc);
      }
    };
    document.addEventListener('keydown', handleEsc);
  }

  mount() {
    document.body.appendChild(this.modal);
  }

  destroy() {
    if (this.modal && this.modal.parentNode) {
      this.modal.parentNode.removeChild(this.modal);
    }
  }
}
